import {
  Card,
  CardContent,
  Box,
  FormControlLabel,
  TextField,
  Grid,
  Tooltip,
  makeStyles,
  MenuItem,
  Select,
} from "@material-ui/core";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import {
  AdvSettingsAccordion,
  AdvSettingsPanelSummary,
  CardStyles,
  CheckboxSmall,
  FieldSetStyles,
  IconLeftExpansionPanelSummary,
  IconStyles,
} from "../../styles";
import { useFormik } from "formik";
import { Autocomplete } from "@material-ui/lab";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import PublishIcon from "@material-ui/icons/Publish";
import { otherSuggestions } from "./SourceDetails-utils";
import { Api } from "../../lib/api";
import { useSnackbar } from "notistack";
import {
  ImportMode,
  RootStatus,
  SourceType,
  Status,
  AdminUserResponse,
} from "@authory-packages/core";
import { SourceLinkExtractionConf } from "./SourceLinkExtractionConf";
import { SourceContentExtractionConf } from "./SourceContentExtractionConf";
import { SourceDateExtractionConf } from "./SourceDateExtractionConf";
import { SourceAuthorMatchConf } from "./SourceAuthorMatchConf";
import { SourceQuickSettingsConf } from "./SourceQuickSettingsConf";
import { SourceDiagnostics } from "./SourceDiagnostics";
import { SourceExtractionAutoComplete } from "./SourceExtractionAutocomplete";
import { useRef, useState } from "react";
import { MoveUrlModal } from "./MoveUrlModal";
import { DeleteSourceModal } from "./DeleteSourceModal";
import { CreateNewSourceModal } from "../CreateNewWebsiteModa";
import { BulkImportPipelineModal } from "./BulkImportPipelineModal";
import { BulkImportPanel } from "../BulkImportModal";
import { SourceWithColorAndParent } from "./SourceManager";
import { SourceSpecificConfig } from "./SourceSpecificConfig";
import { SourceMeta } from "./SourceMeta";
import { SourceActions } from "./SourceActions";
import {
  SourceCrawlProgress,
  showCrawlProgressFor,
} from "./SourceCrawlProgress";
import { useHotkeys } from "react-hotkeys-hook";
import { showRssSourceUrlField } from "./utils";
import { enumToOptions } from "../../lib/enumToOption";

const CardWithDotStyles = makeStyles({
  root: {
    position: "relative",
    "&:after": {
      position: "absolute",
      content: '""',
      top: 6,
      left: 6,
      width: 7,
      height: 7,
      display: "block",
      borderRadius: "50%",
      backgroundColor: "grey",
    },
  },
});

type SourceDetailsProps = {
  source: SourceWithColorAndParent;
  userSlug: string;
  user: AdminUserResponse;
  sourcesLoaded: boolean;
  updateSourcesCallback: (
    canonicalslug: string,
    isDelete?: boolean
  ) => Promise<void>;
};

// Generate status select options from enum
const importOptions = enumToOptions(ImportMode);

export const SourceDetails = ({
  source,
  userSlug,
  user,
  sourcesLoaded,
  updateSourcesCallback,
}: SourceDetailsProps) => {
  const [moveUrlModalOpen, setMoveUrlModalOpen] = useState(false);
  const [createChildModalOpen, setCreateChildModalopen] = useState(false);
  const [deleteSourceModalOpen, setDeleteSourceModalOpen] = useState(false);
  const [importPipelineModalOpen, setImportPipelineModalOpen] = useState(false);
  const [importForceModalOpen, setImportForceModalOpen] = useState(false);
  const [shortcutEnabled, setShortcutEnabled] = useState(false);
  const tabEl = useRef<HTMLDivElement | null>(null);

  const cardStyles = CardStyles();
  const cardWithDotStyles = CardWithDotStyles();
  const iconClasses = IconStyles();
  const fieldSetStyles = FieldSetStyles();

  const { enqueueSnackbar } = useSnackbar();

  let configuration: any = {};

  if (source.type === SourceType.Linkedin) {
    configuration = {
      linkedinCollectArticles:
        source.typeSpecificConfig?.linkedinCollectArticles || false,
      linkedinCollectPosts:
        source.typeSpecificConfig?.linkedinCollectPosts || false,
    };
  } else if (showRssSourceUrlField.includes(source.type)) {
    configuration = {
      rssSourceUrl: source.typeSpecificConfig?.rssSourceUrl || "",
    };
  } else if (source.type === SourceType.Sitemap) {
    configuration = {
      sitemapSourceUrl:
        source.typeSpecificConfig?.sitemapSourceUrl || source.url || "",
    };
  }

  const formik = useFormik({
    initialValues: {
      url: source.url || "",
      notes: source.notes || "",
      active: source.active,
      publicationName: source.name || "",
      stepSleep: source.strategy?.stepSleep || 0,
      nextPage: source.strategy?.nextPage || "",
      loadMore: source.strategy?.loadMore || "",
      authorQuery: source.strategy?.authorQuery || "",
      access: source.strategy?.access || "",
      useProxy: source.strategy?.useProxy || false,
      useShiva: source.strategy?.useShiva || false,
      analyzeLinksToOtherDomains:
        source.strategy?.analyzeLinksToOtherDomains || false,
      aliases: (source.aliases ?? []).join(";") || "",
      skipAuthorCheck: source.strategy?.skipAuthorCheck || false,
      ignoreDiffbotAuthor: source.strategy?.ignoreDiffbotAuthor || false,
      ignoreShivaAuthor: source.strategy?.ignoreShivaAuthor || false,
      ignoreStaticAuthor: source.strategy?.ignoreStaticAuthor || false,
      ignoreStaticDate: source.strategy?.ignoreStaticDate || false,
      ignoreDiffbotDate: source.strategy?.ignoreDiffbotDate || false,
      ignoreDynamicAnalyzeDate:
        source.strategy?.ignoreDynamicAnalyzeDate || false,
      ignoreShivaDate: source.strategy?.ignoreShivaDate || false,
      ignoreDiffbotContent: source.strategy?.ignoreDiffbotContent || false,
      ignoreStaticMetaImages: source.strategy?.ignoreStaticMetaImages || false,
      ignoreDiffbotImages: source.strategy?.ignoreDiffbotImages || false,
      ignoreDynamicAnalyzeImages:
        source.strategy?.ignoreDynamicAnalyzeImages || false,
      diffbotPaginate: source.strategy?.diffbotPaginate || false,
      diffbotForceArticleApi: source.strategy?.diffbotForceArticleApi || false,
      ignoreStaticRedirect: source.strategy?.ignoreStaticRedirect || false,
      ignoreDiffbotCanonical: source.strategy?.ignoreDiffbotCanonical || false,
      ignoreStaticCanonical: source.strategy?.ignoreStaticCanonical || false,
      ignoreShivaDocumentProbability:
        source.strategy?.ignoreShivaDocumentProbability || false,
      ignoreDiffbotResolvedUrl:
        source.strategy?.ignoreDiffbotResolvedUrl || false,
      importMode: source?.importMode || ImportMode.Crawler,
      useYoutubeDl: source.strategy?.useYoutubeDl || false,
      useMetaTitle: source.strategy?.useMetaTitle || false,
      ignoreDuplicateByTextCheck:
        source.strategy?.ignoreDuplicateByTextCheck || false,
      useMetaDescription: source.strategy?.useMetaDescription || false,
      useMetaImage: source.strategy?.useMetaImage || false,
      useLLM: source.strategy?.useLLM || false,
      paginationStrategy: source.strategy?.paginationStrategy || undefined,
      navigationMode: source.strategy?.navigationMode || undefined,
      elementXPath: source.strategy?.elementXPath || undefined,
      containerXPath: source.strategy?.containerXPath || undefined,
      configuration: configuration,
      type: source.type || "",
      importContentFromUser: source.importContentFromUser || false,
      importContentAboutUser: source.importContentAboutUser || false,
    },
    onSubmit: async ({
      url,
      notes,
      active,
      nextPage,
      loadMore,
      authorQuery,
      stepSleep,
      useProxy,
      useShiva,
      analyzeLinksToOtherDomains,
      access,
      aliases,
      skipAuthorCheck,
      ignoreDiffbotAuthor,
      ignoreShivaAuthor,
      ignoreStaticAuthor,
      ignoreStaticDate,
      ignoreDynamicAnalyzeDate,
      ignoreShivaDate,
      ignoreDiffbotDate,
      ignoreDiffbotContent,
      ignoreStaticMetaImages,
      ignoreDiffbotImages,
      ignoreDynamicAnalyzeImages,
      diffbotPaginate,
      diffbotForceArticleApi,
      ignoreStaticRedirect,
      ignoreDiffbotCanonical,
      ignoreStaticCanonical,
      ignoreShivaDocumentProbability,
      ignoreDiffbotResolvedUrl,
      useYoutubeDl,
      useLLM,
      useMetaTitle,
      ignoreDuplicateByTextCheck,
      useMetaImage,
      useMetaDescription,
      configuration,
      publicationName,
      type,
      importContentFromUser,
      importContentAboutUser,
      importMode,
      paginationStrategy,
      navigationMode,
      elementXPath,
      containerXPath,
    }) => {
      try {
        //Update strategy
        let strategyPayload: any = {
          useProxy,
          useShiva,
          access,
          loadMore,
          authorQuery,
          nextPage,
          stepSleep,
          skipAuthorCheck,
          analyzeLinksToOtherDomains,
          ignoreDiffbotAuthor,
          ignoreShivaAuthor,
          ignoreStaticAuthor,
          ignoreStaticDate,
          ignoreDiffbotDate,
          ignoreDynamicAnalyzeDate,
          ignoreShivaDate,
          ignoreDiffbotContent,
          ignoreStaticMetaImages,
          ignoreDiffbotImages,
          ignoreDynamicAnalyzeImages,
          diffbotPaginate,
          diffbotForceArticleApi,
          ignoreStaticRedirect,
          ignoreDiffbotCanonical,
          ignoreStaticCanonical,
          ignoreShivaDocumentProbability,
          ignoreDiffbotResolvedUrl,
          ignoreDuplicateByTextCheck,
          useYoutubeDl,
          useMetaTitle,
          useMetaImage,
          useLLM,
          useMetaDescription,
          paginationStrategy,
          navigationMode,
          elementXPath,
          containerXPath,
        };

        await Api.setStrategy(userSlug, source.canonicalSlug, strategyPayload);

        const updateSource = {
          url,
          notes,
          active,
          configuration,
          publicationName,
          type,
          importMode,
          aliases: aliases.split(";"),
          importContentFromUser,
          importContentAboutUser,
        };

        await Api.updateSource(userSlug, source.canonicalSlug, updateSource);

        await updateSourcesCallback(source.canonicalSlug);

        enqueueSnackbar(`Source ${source.name} updated!`, {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.response.data.message, { variant: "error" });
      }
    },
    enableReinitialize: true,
  });

  const opts: any = {
    enableOnTags: ["INPUT", "TEXTAREA", "SELECT"],
    enabled: shortcutEnabled,
  };

  const onShortcutSave = (e: KeyboardEvent) => {
    e.preventDefault();
    formik.submitForm();
  };

  const onExpandShortcut = (e: KeyboardEvent) => {
    e.preventDefault();
    if (tabEl && tabEl.current) tabEl.current.click();
  };

  const onNewChildWebsite = (e: KeyboardEvent) => {
    e.preventDefault();
    setCreateChildModalopen(true);
  };

  useHotkeys("ctrl+s", (e) => onShortcutSave(e), opts);
  useHotkeys("⌘+s", (e) => onShortcutSave(e), opts);
  useHotkeys("ctrl+d", (e) => onExpandShortcut(e), opts);
  useHotkeys("⌘+d", (e) => onExpandShortcut(e), opts);
  useHotkeys("ctrl+⇧+q", (e) => onNewChildWebsite(e), opts);
  useHotkeys("⌘+⇧+q", (e) => onNewChildWebsite(e), opts);

  const isInProgress =
    source.stage === RootStatus.FullCrawlRunning ||
    source.stage === RootStatus.FullCrawlAnalyzeRunning;

  const SourceExtractionConfTopStates = [
    RootStatus.New,
    RootStatus.FullCrawlAnalyzeRunning,
    RootStatus.FullCrawlRunning,
    RootStatus.ImportFinished,
  ];

  const showSourceExtractionConfTopStates =
    SourceExtractionConfTopStates.includes(source.stage);
  const background =
    source.status === Status.CrawlError
      ? "#feeaea"
      : source.status === Status.CrawlTimeout
        ? "#c4a9cf"
        : undefined;

  return (
    <Card
      onMouseEnter={() => setShortcutEnabled(true)}
      onMouseLeave={() => setShortcutEnabled(false)}
      square={true}
      style={{
        borderLeft: !sourcesLoaded
          ? undefined
          : `2px ${source.parentSource ? "dashed" : "solid"} ${
              source.uiColorCode
            }`,
      }}
      className={formik.dirty ? cardWithDotStyles.root : undefined}
    >
      <CardContent
        className={cardStyles.content}
        style={{ background: background }}
      >
        <Box mt={1}>
          <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item md={12} lg>
                <TextField
                  fullWidth
                  placeholder="Url"
                  name="url"
                  size="small"
                  value={formik.values.url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="outlined"
                  disabled={isInProgress}
                  data-source-form="url"
                />
              </Grid>
              <Grid item>
                <SourceMeta
                  source={source}
                  url={formik.values.url}
                  typeVal={formik.values.type}
                  onTypeBlur={formik.handleBlur}
                  onTypeChange={(e) => {
                    formik.setFieldValue("type", e.target.value);
                    formik.handleChange(e);
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item sm={4}>
                {formik.values.type === SourceType.Web ? (
                  <Select
                    name="importMode"
                    value={formik.values.importMode}
                    onChange={formik.handleChange}
                  >
                    {importOptions.map((st) => (
                      <MenuItem key={st.value} value={st.value}>
                        {st.label}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <span>Strategy: {formik.values.importMode}</span>
                )}
              </Grid>
              <Grid item sm={4}>
                <TextField
                  fullWidth
                  placeholder="Publication Name"
                  name="publicationName"
                  size="small"
                  value={formik.values.publicationName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="outlined"
                />
              </Grid>
              <Grid item sm={4}>
                <TextField
                  fullWidth
                  label="Aliases (Semicolon separated)"
                  name="aliases"
                  value={formik.values.aliases}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item sm={12}>
                <TextField
                  fullWidth
                  placeholder="Notes"
                  name="notes"
                  size="small"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="outlined"
                  rows={3}
                  rowsMax={10}
                  multiline
                />
              </Grid>
              {showSourceExtractionConfTopStates && (
                <SourceExtractionAutoComplete
                  importMode={formik.values.importMode}
                  smSize={3}
                  onInputChange={formik.handleChange}
                  nextPage={formik.values.nextPage}
                  onNextPageChange={(e, value) => {
                    formik.setFieldValue("nextPage", value);
                  }}
                  loadMore={formik.values.loadMore}
                  onLoadMoreChange={(e, value) => {
                    formik.setFieldValue("loadMore", value || "");
                  }}
                  authorQuery={formik.values.authorQuery}
                  onAuthorQueryChange={(e, value) => {
                    formik.setFieldValue("authorQuery", value || "");
                  }}
                  stepSleep={formik.values.stepSleep}
                  onStepSleepChange={(e, value) => {
                    formik.setFieldValue("stepSleep", parseInt(value) || 0);
                  }}
                  containerXPath={formik.values.containerXPath}
                  onContainerXPathChange={(e, value) => {
                    formik.setFieldValue("containerXPath", value || "");
                  }}
                  paginationStrategy={formik.values.paginationStrategy}
                  elementXPath={formik.values.elementXPath}
                  onElementXPathChange={(e, value) => {
                    formik.setFieldValue("elementXPath", value || "");
                  }}
                  navigationMode={formik.values.navigationMode}
                  disabled={isInProgress}
                />
              )}
              {showCrawlProgressFor(source.stage) && (
                <Grid item sm={12}>
                  <SourceCrawlProgress
                    source={source}
                    onRefresh={() =>
                      updateSourcesCallback(source.canonicalSlug)
                    }
                  />
                </Grid>
              )}
              <Grid item sm={7}>
                <SourceQuickSettingsConf
                  active={formik.values.active}
                  onActiveChange={() =>
                    formik.setFieldValue("active", !formik.values.active)
                  }
                  pastContent={source.importPastContent}
                  useProxy={formik.values.useProxy}
                  onUseProxyChange={() =>
                    formik.setFieldValue("useProxy", !formik.values.useProxy)
                  }
                  useShiva={formik.values.useShiva}
                  onUseShivaChange={() =>
                    formik.setFieldValue("useShiva", !formik.values.useShiva)
                  }
                  disabled={isInProgress}
                  sourceType={source.type}
                  useLLM={formik.values.useLLM}
                  onUseLLMChange={() =>
                    formik.setFieldValue("useLLM", !formik.values.useLLM)
                  }
                />
              </Grid>
              <Grid item sm={5}>
                <SourceActions
                  source={source}
                  userSlug={userSlug}
                  onSaveClick={() => formik.submitForm()}
                  onMoveUrlClick={() => setMoveUrlModalOpen(true)}
                  updateSourcesCallback={updateSourcesCallback}
                  onChildCreateClick={() => setCreateChildModalopen(true)}
                  onDeleteClick={() => setDeleteSourceModalOpen(true)}
                />
              </Grid>
            </Grid>
            <AdvSettingsAccordion TransitionProps={{ unmountOnExit: true }}>
              <Grid container spacing={2}>
                <Grid item sm={7} style={{ position: "relative" }}>
                  <IconLeftExpansionPanelSummary
                    style={{ top: -55 }}
                    ref={tabEl}
                    expandIcon={<ExpandMoreIcon fontSize="large" />}
                  ></IconLeftExpansionPanelSummary>
                </Grid>
              </Grid>
              <AdvSettingsPanelSummary>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <fieldset className={fieldSetStyles.container}>
                      <legend>Actions:</legend>
                      <div
                        className={iconClasses.panel}
                        style={{ justifyContent: "flex-start" }}
                      >
                        <Tooltip title="Bulk import (Pipeline)">
                          <PublishIcon
                            className={iconClasses.root}
                            color="action"
                            onClick={() => setImportPipelineModalOpen(true)}
                          />
                        </Tooltip>
                        <Tooltip title="Bulk import (Force)">
                          <CloudUploadIcon
                            className={iconClasses.root}
                            color="action"
                            onClick={() => setImportForceModalOpen(true)}
                          />
                        </Tooltip>
                      </div>
                    </fieldset>
                    <Box mt={1}>
                      <SourceDiagnostics source={source} />
                    </Box>
                    <Box mt={1}>
                      <SourceSpecificConfig
                        source={source}
                        rssSourceUrl={formik.values.configuration.rssSourceUrl}
                        sitemapSourceUrl={
                          formik.values.configuration.sitemapSourceUrl
                        }
                        onInputChangeHandler={formik.handleChange}
                        onInputBlurHandler={formik.handleBlur}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={9}>
                    <fieldset className={fieldSetStyles.container}>
                      <legend>URL import settings:</legend>
                      <Grid container spacing={1}>
                        <Grid item sm={6}>
                          <FormControlLabel
                            control={
                              <CheckboxSmall
                                icon={
                                  <CheckBoxOutlineBlankIcon fontSize="small" />
                                }
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                name="analyzeLinksToOtherDomains"
                                checked={
                                  formik.values.analyzeLinksToOtherDomains
                                }
                                onChange={() =>
                                  formik.setFieldValue(
                                    "analyzeLinksToOtherDomains",
                                    !formik.values.analyzeLinksToOtherDomains,
                                  )
                                }
                              />
                            }
                            label="Follow external links"
                          />
                        </Grid>
                        <Grid item sm={6}>
                          <Autocomplete
                            freeSolo
                            size="small"
                            value={formik.values.access}
                            options={otherSuggestions.map(
                              (option) => option.xpath,
                            )}
                            onChange={(e, value) => {
                              formik.setFieldValue("access", value || "");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Access"
                                variant="outlined"
                                name="access"
                                value={formik.values.access}
                                onChange={formik.handleChange}
                                InputProps={{ ...params.InputProps }}
                              />
                            )}
                          />
                        </Grid>
                        {!showSourceExtractionConfTopStates && (
                          <SourceExtractionAutoComplete
                            importMode={formik.values.importMode}
                            smSize={6}
                            onInputChange={formik.handleChange}
                            nextPage={formik.values.nextPage}
                            onNextPageChange={(e, value) => {
                              formik.setFieldValue("nextPage", value);
                            }}
                            onContainerXPathChange={(e, value) => {
                              formik.setFieldValue(
                                "containerXPath",
                                value || "",
                              );
                            }}
                            onElementXPathChange={(e, value) => {
                              formik.setFieldValue("elementXPath", value || "");
                            }}
                            loadMore={formik.values.loadMore}
                            onLoadMoreChange={(e, value) => {
                              formik.setFieldValue("loadMore", value || "");
                            }}
                            authorQuery={formik.values.authorQuery}
                            onAuthorQueryChange={(e, value) => {
                              formik.setFieldValue("authorQuery", value || "");
                            }}
                            stepSleep={formik.values.stepSleep}
                            onStepSleepChange={(e, value) => {
                              formik.setFieldValue(
                                "stepSleep",
                                parseInt(value) || 0,
                              );
                            }}
                            containerXPath={formik.values.containerXPath}
                            paginationStrategy={
                              formik.values.paginationStrategy
                            }
                            elementXPath={formik.values.elementXPath}
                            navigationMode={formik.values.navigationMode}
                            disabled={isInProgress}
                          />
                        )}
                      </Grid>
                    </fieldset>
                    <Box mt={1}>
                      <SourceAuthorMatchConf
                        skipAuthorCheck={formik.values.skipAuthorCheck}
                        onSkipAuthorCheckChange={() =>
                          formik.setFieldValue(
                            "skipAuthorCheck",
                            !formik.values.skipAuthorCheck,
                          )
                        }
                        ignoreStaticAuthor={formik.values.ignoreStaticAuthor}
                        onIgnoreStaticAuthor={() =>
                          formik.setFieldValue(
                            "ignoreStaticAuthor",
                            !formik.values.ignoreStaticAuthor,
                          )
                        }
                        ignoreDiffbotAuthor={formik.values.ignoreDiffbotAuthor}
                        onIgnoreDiffbotAuthorChange={() =>
                          formik.setFieldValue(
                            "ignoreDiffbotAuthor",
                            !formik.values.ignoreDiffbotAuthor,
                          )
                        }
                        ignoreShivaAuthor={formik.values.ignoreShivaAuthor}
                        onIgnoreShivaAuthor={() =>
                          formik.setFieldValue(
                            "ignoreShivaAuthor",
                            !formik.values.ignoreShivaAuthor,
                          )
                        }
                        importContentFromUser={
                          formik.values.importContentFromUser
                        }
                        onImportContentFromUserChange={() =>
                          formik.setFieldValue(
                            "importContentFromUser",
                            !formik.values.importContentFromUser,
                          )
                        }
                        importContentAboutUser={
                          formik.values.importContentAboutUser
                        }
                        onImportContentAboutUserChange={() =>
                          formik.setFieldValue(
                            "importContentAboutUser",
                            !formik.values.importContentAboutUser,
                          )
                        }
                        sourceType={source.type}
                      />
                    </Box>
                    <Box mt={1}>
                      <SourceDateExtractionConf
                        ignoreStaticDate={formik.values.ignoreStaticDate}
                        onIgnoreStaticDateChange={() =>
                          formik.setFieldValue(
                            "ignoreStaticDate",
                            !formik.values.ignoreStaticDate,
                          )
                        }
                        ignoreDynamicAnalyzeDate={
                          formik.values.ignoreDynamicAnalyzeDate
                        }
                        onIgnoreDynamicAnalyzeDateChange={() =>
                          formik.setFieldValue(
                            "ignoreDynamicAnalyzeDate",
                            !formik.values.ignoreDynamicAnalyzeDate,
                          )
                        }
                        ignoreDiffbotDate={formik.values.ignoreDiffbotDate}
                        onIgnoreDiffbotDateChange={() =>
                          formik.setFieldValue(
                            "ignoreDiffbotDate",
                            !formik.values.ignoreDiffbotDate,
                          )
                        }
                        ignoreShivaDate={formik.values.ignoreShivaDate}
                        onIgnoreShivaDateChange={() =>
                          formik.setFieldValue(
                            "ignoreShivaDate",
                            !formik.values.ignoreShivaDate,
                          )
                        }
                      />
                    </Box>
                    <Box mt={1}>
                      <SourceContentExtractionConf
                        ignoreDiffbotContent={
                          formik.values.ignoreDiffbotContent
                        }
                        onIgnoreDiffbotContentChange={() =>
                          formik.setFieldValue(
                            "ignoreDiffbotContent",
                            !formik.values.ignoreDiffbotContent,
                          )
                        }
                        ignoreDuplicateByTextCheck={
                          formik.values.ignoreDuplicateByTextCheck
                        }
                        onignoreDuplicateByTextCheckChange={() =>
                          formik.setFieldValue(
                            "ignoreDuplicateByTextCheck",
                            !formik.values.ignoreDuplicateByTextCheck,
                          )
                        }
                        ignoreStaticMetaImages={
                          formik.values.ignoreStaticMetaImages
                        }
                        onIgnoreStaticMetaImagesChange={() =>
                          formik.setFieldValue(
                            "ignoreStaticMetaImages",
                            !formik.values.ignoreStaticMetaImages,
                          )
                        }
                        ignoreDiffbotImages={formik.values.ignoreDiffbotImages}
                        onIgnoreDiffbotImagesChange={() =>
                          formik.setFieldValue(
                            "ignoreDiffbotImages",
                            !formik.values.ignoreDiffbotImages,
                          )
                        }
                        ignoreDynamicAnalyzeImages={
                          formik.values.ignoreDynamicAnalyzeImages
                        }
                        onIgnoreDynamicAnalyzeImages={() =>
                          formik.setFieldValue(
                            "ignoreDynamicAnalyzeImages",
                            !formik.values.ignoreDynamicAnalyzeImages,
                          )
                        }
                        diffbotPaginate={formik.values.diffbotPaginate}
                        onDiffbotPaginateChange={() =>
                          formik.setFieldValue(
                            "diffbotPaginate",
                            !formik.values.diffbotPaginate,
                          )
                        }
                        ignoreShivaDocumentProbability={
                          formik.values.ignoreShivaDocumentProbability
                        }
                        onignoreShivaDocumentProbabilityChange={() =>
                          formik.setFieldValue(
                            "ignoreShivaDocumentProbability",
                            !formik.values.ignoreShivaDocumentProbability,
                          )
                        }
                        diffbotForceArticleApi={
                          formik.values.diffbotForceArticleApi
                        }
                        onDiffbotForceArticleApiChange={() =>
                          formik.setFieldValue(
                            "diffbotForceArticleApi",
                            !formik.values.diffbotForceArticleApi,
                          )
                        }
                        ignoreDiffbotResolvedUrl={
                          formik.values.ignoreDiffbotResolvedUrl
                        }
                        onIgnoreDiffbotResolvedUrlChange={() =>
                          formik.setFieldValue(
                            "ignoreDiffbotResolvedUrl",
                            !formik.values.ignoreDiffbotResolvedUrl,
                          )
                        }
                        useYoutubeDl={formik.values.useYoutubeDl}
                        onUseYoutubeDlChange={() =>
                          formik.setFieldValue(
                            "useYoutubeDl",
                            !formik.values.useYoutubeDl,
                          )
                        }
                        useMetaTitle={formik.values.useMetaTitle}
                        onUseMetaTitleChange={() =>
                          formik.setFieldValue(
                            "useMetaTitle",
                            !formik.values.useMetaTitle,
                          )
                        }
                        useMetaDescription={formik.values.useMetaDescription}
                        onUseMetaDescriptionChange={() =>
                          formik.setFieldValue(
                            "useMetaDescription",
                            !formik.values.useMetaDescription,
                          )
                        }
                        useMetaImage={formik.values.useMetaImage}
                        onUseMetaImageChange={() =>
                          formik.setFieldValue(
                            "useMetaImage",
                            !formik.values.useMetaImage,
                          )
                        }
                      />
                    </Box>
                    <Box mt={1}>
                      <SourceLinkExtractionConf
                        ignoreStaticRedirect={
                          formik.values.ignoreStaticRedirect
                        }
                        onIgnoreStaticRedirectChange={() =>
                          formik.setFieldValue(
                            "ignoreStaticRedirect",
                            !formik.values.ignoreStaticRedirect,
                          )
                        }
                        ignoreDiffbotCanonical={
                          formik.values.ignoreDiffbotCanonical
                        }
                        onIgnoreDiffbotCanonicalChange={() =>
                          formik.setFieldValue(
                            "ignoreDiffbotCanonical",
                            !formik.values.ignoreDiffbotCanonical,
                          )
                        }
                        ignoreStaticCanonical={
                          formik.values.ignoreStaticCanonical
                        }
                        onIgnoreStaticCanonicalChange={() =>
                          formik.setFieldValue(
                            "ignoreStaticCanonical",
                            !formik.values.ignoreStaticCanonical,
                          )
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </AdvSettingsPanelSummary>
            </AdvSettingsAccordion>
          </form>
        </Box>
      </CardContent>
      {moveUrlModalOpen && (
        <MoveUrlModal
          open={moveUrlModalOpen}
          onClose={() => setMoveUrlModalOpen(false)}
          userSlug={userSlug}
          websiteSlug={source.canonicalSlug}
        />
      )}
      {deleteSourceModalOpen && (
        <DeleteSourceModal
          open={deleteSourceModalOpen}
          onClose={() => setDeleteSourceModalOpen(false)}
          onSourceDeleted={() =>
            updateSourcesCallback(source.canonicalSlug, true)
          }
          userSlug={userSlug}
          websiteSlug={source.canonicalSlug}
        />
      )}
      {createChildModalOpen && (
        <CreateNewSourceModal
          open={createChildModalOpen}
          onClose={() => setCreateChildModalopen(false)}
          onSourceCreated={() => updateSourcesCallback(source.canonicalSlug)}
          userSlug={userSlug}
          websiteSlug={source.canonicalSlug}
        />
      )}
      {importPipelineModalOpen && (
        <BulkImportPipelineModal
          open={importPipelineModalOpen}
          onClose={() => setImportPipelineModalOpen(false)}
          userSlug={userSlug}
          websiteSlug={source.canonicalSlug}
          onUrlSubmitted={() => updateSourcesCallback(source.canonicalSlug)}
        />
      )}
      {importForceModalOpen && (
        <BulkImportPanel
          open={importForceModalOpen}
          onClose={() => setImportForceModalOpen(false)}
          userSlug={userSlug}
          websiteSlug={source.canonicalSlug}
          onImportFinished={() => updateSourcesCallback(source.canonicalSlug)}
        />
      )}
    </Card>
  );
};
